import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  BookMarked,
  LogOut,
  FileText,
  PackagePlus,
  Percent,
  History,
  Loader2,
  Copy,
  Plus,
  Trash2,
  CheckCircle2,
  Send,
  Clock,
  Printer,
  Share2,
} from "lucide-react";
import LogoMR from "@/components/landing/LogoMR";
import {
  bsGetToken,
  bsMe,
  bsGet,
  bsPost,
  bsClearToken,
  bsFormatError,
} from "@/lib/bookstoreApi";

const SITE = process.env.REACT_APP_BACKEND_URL || "https://metododimare.com";

const TABS = [
  { id: "module", label: "Modulo Conto Vendita", icon: FileText },
  { id: "request", label: "Richiedi Copie", icon: PackagePlus },
  { id: "referral", label: "Codice Sconto 5%", icon: Percent },
  { id: "history", label: "Storico", icon: History },
];

function fmtEuro(n) {
  return `€ ${Number(n || 0).toFixed(2)}`;
}
function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function LibrerieDashboardPage() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("module");
  const [loading, setLoading] = useState(true);
  const [modules, setModules] = useState([]);
  const [requests, setRequests] = useState([]);

  useEffect(() => {
    if (!bsGetToken()) {
      navigate("/librerie", { replace: true });
      return;
    }
    (async () => {
      try {
        const bs = await bsMe();
        setMe(bs);
        const [mm, rr] = await Promise.all([
          bsGet("/bookstore/modules"),
          bsGet("/bookstore/requests"),
        ]);
        setModules(mm.items || []);
        setRequests(rr.items || []);
      } catch (err) {
        toast.error(bsFormatError(err));
        bsClearToken();
        navigate("/librerie", { replace: true });
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const logout = () => {
    bsClearToken();
    toast.success("Sessione chiusa");
    navigate("/librerie");
  };

  const refreshModules = async () => {
    const mm = await bsGet("/bookstore/modules");
    setModules(mm.items || []);
  };
  const refreshRequests = async () => {
    const rr = await bsGet("/bookstore/requests");
    setRequests(rr.items || []);
  };

  if (loading || !me) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#0E7C8F]" size={32} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="bookstore-dashboard">
      {/* Header */}
      <header className="bg-[#0B192C] text-[#F9F8F6] border-b border-[#1E3557]">
        <div className="max-w-[1400px] mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LogoMR size={44} />
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#D4B895]">Area Librerie</p>
              <h1 className="font-serif-display text-xl" data-testid="bs-name">{me.business_name}</h1>
            </div>
          </div>
          <button
            onClick={logout}
            className="inline-flex items-center gap-2 px-4 py-2 border border-[#D4B895] text-[#D4B895] text-xs uppercase tracking-[0.18em] hover:bg-[#D4B895] hover:text-[#0B192C]"
            data-testid="bs-logout"
          >
            <LogOut size={14} /> Esci
          </button>
        </div>
      </header>

      {/* Tabs */}
      <nav className="bg-white border-b border-[#E5E1DA]">
        <div className="max-w-[1400px] mx-auto px-6 flex flex-wrap gap-2 overflow-x-auto">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`bs-tab-${t.id}`}
                className={`inline-flex items-center gap-2 px-4 py-4 text-xs uppercase tracking-[0.15em] border-b-2 transition-colors whitespace-nowrap ${
                  active
                    ? "border-[#0E7C8F] text-[#0E7C8F]"
                    : "border-transparent text-[#5A6472] hover:text-[#0B192C]"
                }`}
              >
                <Icon size={14} />
                {t.label}
              </button>
            );
          })}
        </div>
      </nav>

      <main className="max-w-[1400px] mx-auto px-6 py-10">
        {tab === "module" && (
          <ModuleTab bookstore={me} onSubmitted={refreshModules} />
        )}
        {tab === "request" && (
          <RequestTab bookstore={me} onSubmitted={refreshRequests} />
        )}
        {tab === "referral" && <ReferralTab bookstore={me} />}
        {tab === "history" && (
          <HistoryTab modules={modules} requests={requests} bookstore={me} />
        )}
      </main>
    </div>
  );
}

/* =========================================================
   TAB 1 · Modulo Conto Vendita
   ========================================================= */
function ModuleTab({ bookstore, onSubmitted }) {
  const [items, setItems] = useState([{ title: "Metodo di Mare", price: 19.9, quantity: 1 }]);
  const [retention, setRetention] = useState(30);
  const [signedBy, setSignedBy] = useState(bookstore.contact_person || "");
  const [moduleDate, setModuleDate] = useState(new Date().toISOString().slice(0, 10));
  const [location, setLocation] = useState(bookstore.city || "");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const totals = useMemo(() => {
    let subtotal = 0;
    let books = 0;
    items.forEach((it) => {
      subtotal += Number(it.price || 0) * Number(it.quantity || 0);
      books += Number(it.quantity || 0);
    });
    subtotal = Math.round(subtotal * 100) / 100;
    const retention_amt = Math.round(subtotal * (retention / 100) * 100) / 100;
    const author_amt = Math.round((subtotal - retention_amt) * 100) / 100;
    return { subtotal, books, retention_amt, author_amt };
  }, [items, retention]);

  const setItem = (i, k, v) => {
    setItems((arr) => arr.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  };
  const addRow = () => setItems((a) => [...a, { title: "", price: 19.9, quantity: 1 }]);
  const removeRow = (i) => setItems((a) => a.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!items.length) {
      toast.error("Aggiungi almeno un titolo");
      return;
    }
    for (const it of items) {
      if (!it.title.trim() || !it.price || !it.quantity) {
        toast.error("Compila titolo, prezzo e quantità per ogni riga");
        return;
      }
    }
    setSubmitting(true);
    try {
      await bsPost("/bookstore/module", {
        items: items.map((it) => ({
          title: it.title.trim(),
          price: Number(it.price),
          quantity: Number(it.quantity),
        })),
        retention_pct: Number(retention),
        signed_by: signedBy || null,
        module_date: moduleDate || null,
        location: location || null,
        notes: notes || null,
      });
      toast.success("Modulo registrato e email di conferma inviata");
      onSubmitted?.();
      setItems([{ title: "Metodo di Mare", price: 19.9, quantity: 1 }]);
      setNotes("");
    } catch (err) {
      toast.error(bsFormatError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-[#E5E1DA] p-8" data-testid="module-form">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="font-serif-display text-3xl text-[#0B192C]">Modulo Libri in Conto Vendita</h2>
          <p className="text-sm text-[#5A6472] mt-1">
            Versione digitale del modulo cartaceo. Alla firma ricevi via email la
            conferma con dettaglio importi e quote.
          </p>
        </div>
        <div className="text-right text-xs text-[#5A6472]">
          <p><strong>{bookstore.business_name}</strong></p>
          {bookstore.vat_number && <p>P.IVA: {bookstore.vat_number}</p>}
          {bookstore.tax_code && <p>C.F.: {bookstore.tax_code}</p>}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-6 pb-6 border-b border-[#E5E1DA]">
        <div>
          <label className="block text-[10px] uppercase tracking-[0.18em] text-[#5A6472] mb-2">
            Data modulo
          </label>
          <input
            type="date"
            value={moduleDate}
            onChange={(e) => setModuleDate(e.target.value)}
            className="w-full px-4 py-3 border border-[#E5E1DA] focus:border-[#0E7C8F] outline-none"
            data-testid="module-date"
          />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.18em] text-[#5A6472] mb-2">
            Luogo
          </label>
          <input
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            className="w-full px-4 py-3 border border-[#E5E1DA] focus:border-[#0E7C8F] outline-none"
            data-testid="module-location"
          />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.18em] text-[#5A6472] mb-2">
            Firmato da
          </label>
          <input
            value={signedBy}
            onChange={(e) => setSignedBy(e.target.value)}
            className="w-full px-4 py-3 border border-[#E5E1DA] focus:border-[#0E7C8F] outline-none"
            data-testid="module-signed-by"
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse" data-testid="module-items-table">
          <thead>
            <tr className="bg-[#0B192C] text-[#F9F8F6] text-xs uppercase tracking-[0.15em]">
              <th className="text-left px-4 py-3 border border-[#0B192C]">Titolo</th>
              <th className="text-right px-4 py-3 border border-[#0B192C] w-32">Prezzo (€)</th>
              <th className="text-center px-4 py-3 border border-[#0B192C] w-24">Q.tà</th>
              <th className="text-right px-4 py-3 border border-[#0B192C] w-32">Subtotale</th>
              <th className="w-12 border border-[#0B192C]"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => {
              const sub = Number(it.price || 0) * Number(it.quantity || 0);
              return (
                <tr key={i}>
                  <td className="border border-[#E5E1DA] p-0">
                    <input
                      value={it.title}
                      onChange={(e) => setItem(i, "title", e.target.value)}
                      className="w-full px-4 py-3 outline-none"
                      placeholder="Titolo del libro"
                      data-testid={`item-title-${i}`}
                    />
                  </td>
                  <td className="border border-[#E5E1DA] p-0">
                    <input
                      type="number"
                      step="0.01"
                      value={it.price}
                      onChange={(e) => setItem(i, "price", e.target.value)}
                      className="w-full px-4 py-3 outline-none text-right"
                      data-testid={`item-price-${i}`}
                    />
                  </td>
                  <td className="border border-[#E5E1DA] p-0">
                    <input
                      type="number"
                      value={it.quantity}
                      onChange={(e) => setItem(i, "quantity", e.target.value)}
                      className="w-full px-4 py-3 outline-none text-center"
                      data-testid={`item-qty-${i}`}
                    />
                  </td>
                  <td className="border border-[#E5E1DA] px-4 py-3 text-right font-mono">
                    {fmtEuro(sub)}
                  </td>
                  <td className="border border-[#E5E1DA] text-center">
                    {items.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeRow(i)}
                        className="text-[#B23A48] p-2"
                        data-testid={`item-remove-${i}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <button
        type="button"
        onClick={addRow}
        className="mt-3 inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-[#0E7C8F] hover:text-[#0B192C]"
        data-testid="add-item-row"
      >
        <Plus size={14} /> Aggiungi titolo
      </button>

      <div className="grid md:grid-cols-2 gap-6 mt-8">
        <div>
          <label className="block text-[10px] uppercase tracking-[0.18em] text-[#5A6472] mb-2">
            % Trattenuta libreria
          </label>
          <input
            type="number"
            min="0"
            max="100"
            step="0.1"
            value={retention}
            onChange={(e) => setRetention(e.target.value)}
            className="w-full px-4 py-3 border border-[#E5E1DA] focus:border-[#0E7C8F] outline-none"
            data-testid="module-retention"
          />
          <div className="mt-4">
            <label className="block text-[10px] uppercase tracking-[0.18em] text-[#5A6472] mb-2">
              Note (opzionale)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-4 py-3 border border-[#E5E1DA] focus:border-[#0E7C8F] outline-none"
              data-testid="module-notes"
            />
          </div>
        </div>

        <div className="bg-[#F4EDE0] p-6 space-y-3" data-testid="module-totals">
          <div className="flex justify-between text-sm">
            <span>Totale libri:</span>
            <strong>{totals.books}</strong>
          </div>
          <div className="flex justify-between text-sm">
            <span>Subtotale:</span>
            <strong className="font-mono">{fmtEuro(totals.subtotal)}</strong>
          </div>
          <div className="flex justify-between text-sm">
            <span>Trattenuta libreria ({retention}%):</span>
            <strong className="font-mono">{fmtEuro(totals.retention_amt)}</strong>
          </div>
          <div className="flex justify-between text-base pt-3 border-t border-[#E5E1DA]">
            <span>Quota autore:</span>
            <strong className="font-mono text-[#0E7C8F]">{fmtEuro(totals.author_amt)}</strong>
          </div>
        </div>
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="mt-8 w-full md:w-auto md:min-w-[260px] px-8 py-4 bg-[#0B192C] text-[#F9F8F6] text-xs uppercase tracking-[0.2em] hover:bg-[#1E3557] disabled:opacity-60"
        data-testid="module-submit"
      >
        {submitting ? (
          <Loader2 className="inline w-4 h-4 mr-2 animate-spin" />
        ) : (
          <CheckCircle2 className="inline w-4 h-4 mr-2" />
        )}
        Firma e Invia Modulo
      </button>
    </form>
  );
}

/* =========================================================
   TAB 2 · Richiedi Copie
   ========================================================= */
function RequestTab({ bookstore, onSubmitted }) {
  const [quantity, setQuantity] = useState(10);
  const [notes, setNotes] = useState("");
  const [shippingAddress, setShippingAddress] = useState(
    `${bookstore.address || ""}, ${bookstore.postal_code || ""} ${bookstore.city || ""} ${bookstore.province || ""}`.trim()
  );
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!quantity || quantity < 1) {
      toast.error("Inserisci una quantità valida");
      return;
    }
    setSubmitting(true);
    try {
      await bsPost("/bookstore/request", {
        quantity: Number(quantity),
        notes: notes || null,
        shipping_address: shippingAddress || null,
      });
      toast.success("Richiesta inviata! Riceverai una conferma via email.");
      setNotes("");
      onSubmitted?.();
    } catch (err) {
      toast.error(bsFormatError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="max-w-2xl bg-white border border-[#E5E1DA] p-8" data-testid="request-form">
      <h2 className="font-serif-display text-3xl text-[#0B192C] mb-2">Richiedi Copie del Libro</h2>
      <p className="text-sm text-[#5A6472] mb-6">
        Compila il form: verrà inviata immediatamente una notifica a
        metododimare@gmail.com per organizzare la spedizione.
      </p>
      <div className="space-y-4">
        <div>
          <label className="block text-[10px] uppercase tracking-[0.18em] text-[#5A6472] mb-2">
            Numero copie richieste *
          </label>
          <input
            type="number"
            min="1"
            required
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            className="w-full px-4 py-3 border border-[#E5E1DA] focus:border-[#0E7C8F] outline-none text-2xl font-mono text-[#0B192C]"
            data-testid="request-quantity"
          />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.18em] text-[#5A6472] mb-2">
            Indirizzo spedizione
          </label>
          <input
            value={shippingAddress}
            onChange={(e) => setShippingAddress(e.target.value)}
            className="w-full px-4 py-3 border border-[#E5E1DA] focus:border-[#0E7C8F] outline-none"
            data-testid="request-shipping"
          />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.18em] text-[#5A6472] mb-2">
            Note (opzionale)
          </label>
          <textarea
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full px-4 py-3 border border-[#E5E1DA] focus:border-[#0E7C8F] outline-none"
            placeholder="Preferenze corriere, tempistiche, contatti aggiuntivi..."
            data-testid="request-notes"
          />
        </div>
      </div>
      <button
        type="submit"
        disabled={submitting}
        className="mt-6 px-8 py-4 bg-[#E89B6C] text-[#0B192C] text-xs uppercase tracking-[0.2em] hover:bg-[#D4B895] disabled:opacity-60"
        data-testid="request-submit"
      >
        {submitting ? (
          <Loader2 className="inline w-4 h-4 mr-2 animate-spin" />
        ) : (
          <Send className="inline w-4 h-4 mr-2" />
        )}
        Invia Richiesta
      </button>
    </form>
  );
}

/* =========================================================
   TAB 3 · Referral 5%
   ========================================================= */
function ReferralTab({ bookstore }) {
  const code = bookstore.referral_code || "—";
  const link = `${SITE}/?ref=${encodeURIComponent(code)}`;
  const message = `Ciao! Se acquisti "Metodo di Mare" di Michele Roccaro tramite ${bookstore.business_name} usando il codice ${code}, hai il 5% di sconto. Info: ${link}`;

  const copy = (text, label) => {
    navigator.clipboard?.writeText(text);
    toast.success(`${label} copiato`);
  };

  return (
    <div className="max-w-3xl">
      <div className="bg-[#0B192C] text-[#F9F8F6] p-10 mb-6">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#D4B895] mb-4">Il tuo codice sconto</p>
        <p className="font-mono text-5xl font-bold tracking-widest text-[#D4B895] mb-6" data-testid="referral-code">
          {code}
        </p>
        <p className="text-sm text-[#F9F8F6]/80 max-w-lg">
          Girato a librerie amiche e clienti, dà loro il <strong>5% di sconto</strong> sul libro
          "Metodo di Mare". Ogni utilizzo viene tracciato al tuo account.
        </p>
      </div>

      <div className="bg-white border border-[#E5E1DA] p-6 mb-4">
        <p className="text-[10px] uppercase tracking-[0.18em] text-[#5A6472] mb-2">Link diretto con sconto</p>
        <div className="flex items-center gap-2">
          <code className="flex-1 px-4 py-3 bg-[#F4EDE0] font-mono text-sm break-all" data-testid="referral-link">
            {link}
          </code>
          <button
            onClick={() => copy(link, "Link")}
            className="px-4 py-3 bg-[#0E7C8F] text-white text-xs uppercase tracking-[0.15em] hover:bg-[#0B192C]"
            data-testid="copy-link-btn"
          >
            <Copy size={14} />
          </button>
        </div>
      </div>

      <div className="bg-white border border-[#E5E1DA] p-6">
        <p className="text-[10px] uppercase tracking-[0.18em] text-[#5A6472] mb-2">Messaggio pronto da inviare</p>
        <textarea
          readOnly
          value={message}
          rows={4}
          className="w-full px-4 py-3 border border-[#E5E1DA] bg-[#F9F8F6] font-sans text-sm"
          data-testid="referral-message"
        />
        <div className="flex gap-2 mt-3">
          <button
            onClick={() => copy(message, "Messaggio")}
            className="inline-flex items-center gap-2 px-4 py-2 border border-[#0B192C] text-[#0B192C] text-xs uppercase tracking-[0.15em] hover:bg-[#0B192C] hover:text-[#F9F8F6]"
            data-testid="copy-msg-btn"
          >
            <Copy size={14} /> Copia
          </button>
          <a
            href={`https://wa.me/?text=${encodeURIComponent(message)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#25D366] text-white text-xs uppercase tracking-[0.15em]"
            data-testid="share-whatsapp"
          >
            <Share2 size={14} /> WhatsApp
          </a>
        </div>
      </div>
    </div>
  );
}

/* =========================================================
   TAB 4 · Storico
   ========================================================= */
function HistoryTab({ modules, requests, bookstore }) {
  return (
    <div className="space-y-10">
      <section>
        <h2 className="font-serif-display text-2xl text-[#0B192C] mb-4">Moduli firmati ({modules.length})</h2>
        {!modules.length ? (
          <p className="text-sm text-[#5A6472]">Nessun modulo registrato.</p>
        ) : (
          <div className="space-y-3" data-testid="history-modules">
            {modules.map((m) => (
              <ModuleCard key={m.id} module={m} bookstore={bookstore} />
            ))}
          </div>
        )}
      </section>

      <section>
        <h2 className="font-serif-display text-2xl text-[#0B192C] mb-4">Richieste copie ({requests.length})</h2>
        {!requests.length ? (
          <p className="text-sm text-[#5A6472]">Nessuna richiesta effettuata.</p>
        ) : (
          <div className="space-y-3" data-testid="history-requests">
            {requests.map((r) => (
              <div key={r.id} className="bg-white border border-[#E5E1DA] p-5 flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#5A6472]">{fmtDate(r.created_at)}</p>
                  <p className="text-lg font-semibold text-[#0B192C]">{r.quantity} copie</p>
                  {r.notes && <p className="text-sm text-[#5A6472] mt-1">{r.notes}</p>}
                </div>
                <span
                  className="px-3 py-1 text-[10px] uppercase tracking-[0.15em] font-semibold"
                  style={{
                    background:
                      r.status === "delivered" ? "#0B7C5C" :
                      r.status === "shipped" ? "#0E7C8F" :
                      r.status === "approved" ? "#3A4F6B" :
                      r.status === "cancelled" ? "#B23A48" :
                      "#E89B6C",
                    color: "#F9F8F6",
                  }}
                  data-testid={`req-status-${r.id}`}
                >
                  {r.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function ModuleCard({ module, bookstore }) {
  const [open, setOpen] = useState(false);
  const printModule = () => {
    const win = window.open("", "_blank");
    if (!win) return;
    const rows = module.items
      .map(
        (it) =>
          `<tr><td>${it.title}</td><td style="text-align:right">€ ${Number(it.price).toFixed(2)}</td><td style="text-align:center">${it.quantity}</td><td style="text-align:right">€ ${Number(it.subtotal).toFixed(2)}</td></tr>`
      )
      .join("");
    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Modulo Conto Vendita</title>
<style>body{font-family:Georgia,serif;max-width:720px;margin:40px auto;padding:20px;color:#0B192C}
h1{font-size:22px;text-align:center;text-transform:uppercase;letter-spacing:2px}
table{width:100%;border-collapse:collapse;margin:20px 0}th,td{border:1px solid #ccc;padding:8px}
th{background:#0B192C;color:#F9F8F6}.hdr{margin-bottom:20px}.hdr p{margin:2px 0;font-size:13px}
.totals td{background:#F4EDE0}.sign{margin-top:60px;border-top:1px solid #0B192C;padding-top:8px;text-align:right}</style></head><body>
<div class="hdr"><p><strong>${bookstore.business_name}</strong></p>
<p>${bookstore.contact_person || ''}</p>
<p>${bookstore.address || ''}, ${bookstore.postal_code || ''} ${bookstore.city || ''} ${bookstore.province || ''}</p>
<p>P.IVA: ${bookstore.vat_number || '-'} · C.F.: ${bookstore.tax_code || '-'}</p>
<p>Email: ${bookstore.email}</p></div>
<h1>Modulo Libri in Conto Vendita</h1>
<p>In data <strong>${module.module_date}</strong>, presso <strong>${bookstore.business_name}</strong>,
il/la sig. <strong>Michele Roccaro</strong> lascia in conto vendita:</p>
<table><thead><tr><th style="text-align:left">Titolo</th><th>Prezzo</th><th>Q.tà</th><th>Subtotale</th></tr></thead>
<tbody>${rows}</tbody>
<tfoot class="totals">
<tr><td colspan="3" style="text-align:right"><strong>Totale libri:</strong></td><td style="text-align:right">${module.totals.total_books}</td></tr>
<tr><td colspan="3" style="text-align:right"><strong>Subtotale:</strong></td><td style="text-align:right">€ ${module.totals.subtotal.toFixed(2)}</td></tr>
<tr><td colspan="3" style="text-align:right"><strong>Trattenuta libreria (${module.totals.bookstore_retention_pct}%):</strong></td><td style="text-align:right">€ ${module.totals.bookstore_retention_amount.toFixed(2)}</td></tr>
<tr><td colspan="3" style="text-align:right"><strong>Quota autore:</strong></td><td style="text-align:right"><strong>€ ${module.totals.author_amount.toFixed(2)}</strong></td></tr>
</tfoot></table>
<p><em>La libreria tratterrà alla vendita il ${module.totals.bookstore_retention_pct}%.</em></p>
<p>${module.location || bookstore.city}, ${module.module_date}</p>
<div class="sign">In fede, ${module.signed_by || bookstore.contact_person}<br/>${bookstore.business_name}</div>
</body></html>`);
    win.document.close();
    win.print();
  };

  return (
    <div className="bg-white border border-[#E5E1DA]" data-testid={`module-card-${module.id}`}>
      <div className="p-5 flex items-center justify-between cursor-pointer" onClick={() => setOpen(!open)}>
        <div>
          <p className="text-sm text-[#5A6472]">
            <Clock className="inline w-3 h-3 mr-1" />
            {fmtDate(module.created_at)} · Data modulo: {module.module_date}
          </p>
          <p className="text-lg font-semibold text-[#0B192C]">
            {module.totals.total_books} libri · {fmtEuro(module.totals.subtotal)}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-[#0E7C8F]">Quota autore: <strong>{fmtEuro(module.totals.author_amount)}</strong></span>
          <button
            onClick={(e) => { e.stopPropagation(); printModule(); }}
            className="p-2 border border-[#0B192C] hover:bg-[#0B192C] hover:text-[#F9F8F6] text-[#0B192C]"
            data-testid={`print-module-${module.id}`}
          >
            <Printer size={14} />
          </button>
        </div>
      </div>
      {open && (
        <div className="border-t border-[#E5E1DA] px-5 py-4 bg-[#F9F8F6]">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-[#5A6472]">
                <th className="pb-2">Titolo</th>
                <th className="pb-2 text-right">Prezzo</th>
                <th className="pb-2 text-center">Q.tà</th>
                <th className="pb-2 text-right">Subtotale</th>
              </tr>
            </thead>
            <tbody>
              {module.items.map((it, i) => (
                <tr key={i} className="border-t border-[#E5E1DA]">
                  <td className="py-2">{it.title}</td>
                  <td className="py-2 text-right font-mono">{fmtEuro(it.price)}</td>
                  <td className="py-2 text-center">{it.quantity}</td>
                  <td className="py-2 text-right font-mono">{fmtEuro(it.subtotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {module.notes && (
            <p className="text-sm text-[#5A6472] mt-4 pt-4 border-t border-[#E5E1DA]">
              <strong>Note:</strong> {module.notes}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
